// Orchestrates the presign -> S3 PUT -> ready-to-submit upload flow for the
// public Artwork Intake form. The submit POST is a separate call made by the
// component once `uploadAll()` resolves successfully.
//
// S3 PUTs use XMLHttpRequest (not fetch) so upload progress can be wired
// through `xhr.upload.onprogress`. The presigned URL is signed for the exact
// Content-Type sent on presign, so the PUT MUST send the same value back.

const FILE_STATE = {
  QUEUED: "queued",
  UPLOADING: "uploading",
  DONE: "done",
  FAILED: "failed",
};

function extractErrorMessage(e) {
  const raw =
    (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
    (e && e.message) ||
    "";
  if (!raw) return "Upload failed";
  const firstLine = String(raw).split("\n", 1)[0];
  const stripped = firstLine.replace(/^.*Exception:\s*/, "");
  return stripped || firstLine || "Upload failed";
}

function generateDraftSubmissionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / test envs.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function putToS3(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("S3 upload failed (network error)"));
    xhr.onabort = () => reject(new Error("S3 upload aborted"));
    xhr.send(file);
  });
}

class ArtworkIntakeUploader {
  constructor({ formId, embedToken, service, files, onFileChange }) {
    this.formId = formId;
    this.embedToken = embedToken;
    this.service = service;
    this.onFileChange = onFileChange || (() => {});
    this.draftSubmissionId = generateDraftSubmissionId();
    this.items = files.map((file, index) => ({
      index,
      file,
      name: file.name,
      bytes: file.size,
      contentType: file.type || "application/octet-stream",
      state: FILE_STATE.QUEUED,
      progress: 0,
      key: null,
      error: null,
    }));
  }

  _update(item, patch) {
    Object.assign(item, patch);
    this.onFileChange([...this.items]);
  }

  async _uploadOne(item) {
    try {
      this._update(item, { state: FILE_STATE.UPLOADING, progress: 0 });
      const { url, key } = await this.service.presignUpload(
        this.formId,
        {
          draftSubmissionId: this.draftSubmissionId,
          filename: item.name,
          contentType: item.contentType,
          fileBytes: item.bytes,
        },
        this.embedToken
      );
      await putToS3(url, item.file, item.contentType, (pct) => {
        this._update(item, { progress: pct });
      });
      this._update(item, { state: FILE_STATE.DONE, progress: 100, key });
    } catch (e) {
      const message = extractErrorMessage(e);
      this._update(item, { state: FILE_STATE.FAILED, error: message });
      const wrapped = new Error(message);
      wrapped.original = e;
      wrapped.file = item.name;
      throw wrapped;
    }
  }

  async uploadAll() {
    // Sequential to keep progress UI simple. Switch to Promise.all if parallel
    // S3 PUTs are desired and the backend can sign concurrently.
    for (const item of this.items) {
      // eslint-disable-next-line no-await-in-loop
      await this._uploadOne(item);
    }
    return this.items.map((it) => ({
      key: it.key,
      name: it.name,
      contentType: it.contentType,
      bytes: it.bytes,
    }));
  }
}

export { ArtworkIntakeUploader, FILE_STATE, generateDraftSubmissionId };
export default ArtworkIntakeUploader;
